import React, { useEffect, useState } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import "../../../../styles/useCustomScrollbar";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";
import {
  CalorieTimeSplit,
  CalorieIntakeVsGoal,
  MACRO_TABS,
  MACRO_CONFIG,
  MacroTab,
  TimeSplitDay,
  IntakeVsGoalDay,
} from "./WeeklyMacroIntakeDistribution";

export interface FoodItem {
  name: string;
  quantity: string;
  kcal: number;
  pro?: number;
  carb?: number;
  fat?: number;
  gl?: number;
}

export interface Meal {
  name: string; // e.g. "BREAKFAST"
  time: string; // e.g. "7:45 am"
  items: FoodItem[];
}

export interface DayLog {
  dayShort: string; // e.g. "Mo"
  dayLabel: string; // e.g. "MO, 20 MAY"
  mealType: string; // e.g. "Regular"
  goal: number; // calorie goal
  goalPro?: number;
  goalCarb?: number;
  goalFat?: number;
  goalGl?: number;
  meals: Meal[];
}

type MacroKey = "kcal" | "pro" | "carb" | "fat" | "gl";

const macroKeyForTab = (tab: MacroTab): MacroKey => {
  switch (tab) {
    case "Calories":    return "kcal";
    case "Adj Protein": return "pro";
    case "Carbs":       return "carb";
    case "Fat":         return "fat";
    case "GL":          return "gl";
  }
};

const macroUnitForTab = (tab: MacroTab): string =>
  tab === "Calories" ? "kcal" : tab === "GL" ? "" : "g";

const itemValue = (item: FoodItem, key: MacroKey): number =>
  (item[key] ?? 0) as number;

const dayGoal = (day: DayLog, key: MacroKey): number => {
  switch (key) {
    case "kcal": return day.goal;
    case "pro":  return day.goalPro ?? 0;
    case "carb": return day.goalCarb ?? 0;
    case "fat":  return day.goalFat ?? 0;
    case "gl":   return day.goalGl ?? 0;
  }
};

interface WeeklyMacroDetailPopupProps {
  open: boolean;
  onClose: () => void;
  activeTab: MacroTab;
  onTabChange: (tab: MacroTab) => void;
  timeSplit: TimeSplitDay[];
  intakeVsGoal: IntakeVsGoalDay[];
  days?: DayLog[];
}

// ── Helpers ───────────────────────────────────────────────────────────────
const mealTotal = (meal: Meal, key: MacroKey) =>
  meal.items.reduce((s, i) => s + itemValue(i, key), 0);
const dayTotal = (day: DayLog, key: MacroKey) =>
  day.meals.reduce((s, m) => s + mealTotal(m, key), 0);
const dayHighest = (day: DayLog, key: MacroKey): FoodItem | null => {
  let best: FoodItem | null = null;
  day.meals.forEach((m) =>
    m.items.forEach((it) => {
      if (!best || itemValue(it, key) > itemValue(best, key)) best = it;
    }),
  );
  return best;
};

// ── Day row ───────────────────────────────────────────────────────────────
const DayRow: React.FC<{
  day: DayLog;
  expanded: boolean;
  onToggle: () => void;
  activeTab: MacroTab;
}> = ({ day, expanded, onToggle, activeTab }) => {
  const key = macroKeyForTab(activeTab);
  const unit = macroUnitForTab(activeTab);
  const unitLabel = unit ? ` ${unit}` : "";

  const total = dayTotal(day, key);
  const goal = dayGoal(day, key);
  const highest = dayHighest(day, key);
  const highestVal = highest ? itemValue(highest, key) : 0;
  const highestPct = highest && total > 0 ? Math.round((highestVal / total) * 100) : 0;

  return (
    <div className="border-b border-[#F3F4F6] last:border-b-0">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#FAFAFA] transition text-left"
      >
        <div className="flex items-center gap-3 min-w-[220px]">
          <span className="text-[13px] font-semibold uppercase tracking-wide text-[#1F1F1F] font-inter">
            {day.dayLabel}
          </span>
          <span className="text-[11px] font-medium text-[#0DB5B5] border border-[#0DB5B5] rounded-full px-2 py-0.5 font-inter">
            {day.mealType}
          </span>
        </div>

        <div className="flex-1 text-[13px] font-inter">
          {highest && (
            <span className="text-[#8D8D8D]">
              Highest today{" "}
              <span className="text-[#1F1F1F] font-semibold">{highest.name}</span>{" "}
              <span className="text-[#8D8D8D]">
                : {highestVal}{unitLabel} ({highestPct}% of day)
              </span>
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[13px] font-inter">
            <span className="text-[#1F1F1F] font-semibold">{total}</span>
            <span className="text-[#8D8D8D]">{unitLabel} / {goal}{unitLabel}</span>
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-[#8D8D8D]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#8D8D8D]" />
          )}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-5 pb-4">
          {day.meals.map((meal) => {
            const sortedItems = [...meal.items].sort(
              (a, b) => itemValue(b, key) - itemValue(a, key),
            );
            const mealSum = mealTotal(meal, key);
            return (
              <div key={meal.name} className="mt-3 first:mt-1">
                {/* Meal heading */}
                <div className="flex items-center justify-between py-2.5 bg-[#F9FAFB] rounded-md px-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[13px] font-semibold uppercase tracking-wide text-[#1F1F1F] font-inter">
                      {meal.name}
                    </span>
                    <span className="text-[12px] text-[#8D8D8D] font-inter">{meal.time}</span>
                  </div>
                  <span className="text-[13px] font-semibold text-[#1F1F1F] font-inter">
                    {mealSum}{unitLabel}
                  </span>
                </div>

                {/* Items */}
                <div className="divide-y divide-[#F3F4F6]">
                  {sortedItems.map((item, i) => {
                    const isHighestOfDay = highest && item.name === highest.name;
                    return (
                      <div
                        key={`${item.name}-${i}`}
                        className={`flex items-center justify-between px-3 py-3 font-inter ${
                          isHighestOfDay ? "bg-[#F0FAFA]" : ""
                        }`}
                      >
                        <div className="flex items-baseline gap-2">
                          <span
                            className={`text-[13px] ${
                              isHighestOfDay
                                ? "text-[#0DB5B5] font-semibold"
                                : "text-[#1F1F1F] font-medium"
                            }`}
                          >
                            {item.name}
                          </span>
                          <span className="text-[12px] text-[#8D8D8D]">{item.quantity}</span>
                        </div>
                        <span
                          className={`text-[13px] font-medium ${
                            isHighestOfDay ? "text-[#0DB5B5] font-semibold" : "text-[#1F1F1F]"
                          }`}
                        >
                          {itemValue(item, key)}{unitLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Popup ─────────────────────────────────────────────────────────────────
const WeeklyMacroDetailPopup: React.FC<WeeklyMacroDetailPopupProps> = ({
  open,
  onClose,
  activeTab,
  onTabChange,
  timeSplit,
  intakeVsGoal,
  days = [],
}) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useCustomScrollbar();

  // Reset expansion whenever the popup is reopened
  useEffect(() => {
    if (open) setExpanded(new Set());
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const toggle = (i: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-[1200px] max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-[#F3F4F6]">
          <div>
            <h2 className="text-[18px] 3xl:text-[20px] font-semibold text-[#1F1F1F] font-inter">
              Weekly Macro Intake &amp; Distribution Sources
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-[#F3F4F6] transition text-[#1F1F1F]"
            aria-label="close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body (scroll) */}
        <div className="flex-1 overflow-y-auto custom-scrollbar-overlay px-6 py-4">
          {/* Tabs */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {MACRO_TABS.map((tab) => {
              const active = tab === activeTab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => onTabChange(tab)}
                  className={`px-4 py-1.5 rounded-full text-[12px] 3xl:text-[14px] font-inter font-medium border transition ${
                    active
                      ? "bg-[#1F1F1F] text-white border-[#1F1F1F]"
                      : "bg-white text-[#1F1F1F] border-[#E5E7EB] hover:border-[#8D8D8D]"
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <CalorieTimeSplit
              data={timeSplit}
              title={MACRO_CONFIG[activeTab].timeSplitTitle}
            />
            <CalorieIntakeVsGoal
              data={intakeVsGoal}
              title={MACRO_CONFIG[activeTab].intakeTitle}
              goalMetColor={MACRO_CONFIG[activeTab].goalMetColor}
              unit={MACRO_CONFIG[activeTab].unit}
            />
          </div>

          {/* Weekly meal list */}
          <div className="border border-[#F3F4F6] rounded-xl overflow-hidden">
            {days.map((day, i) => (
              <DayRow
                key={day.dayLabel}
                day={day}
                expanded={expanded.has(i)}
                onToggle={() => toggle(i)}
                activeTab={activeTab}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyMacroDetailPopup;
